import LinkIcon from "@mui/icons-material/Link";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface FactCheckFinding {
  id: number;
  section_heading: string;
  category:
    | "factual_error"
    | "harmful_to_client"
    | "link_issue"
    | "outdated_info"
    | "unverifiable_claim"
    | string;
  severity: "critical" | "warning" | "suggestion" | string;
  original_text: string;
  replacement_text: string | null;
  reasoning: string;
  source: string;
  action: "replace" | "remove" | "flag_for_review" | string;
  applied?: boolean;
  status?: "pending" | "applied" | "declined";
  locator?: {
    start_char?: number | null;
    end_char?: number | null;
    context_before?: string | null;
    context_after?: string | null;
    match_strategy?: string;
  };
  // Captured when the finding is applied. Preserves the article-side state
  // separately from `original_text` (which is the audit record of what was
  // there before). Re-anchoring the green highlight on reload uses these.
  applied_text?: string;
  // Intentionally NO start_char/end_char here — positions become stale the
  // moment any other edit happens. Use context_before/after for re-anchoring.
  applied_locator?: {
    context_before?: string | null;
    context_after?: string | null;
    applied_at?: string;
  };
}

export interface AutoReviewDecision {
  findingId: number;
  decision: "accept" | "reject" | "escalate" | string;
  finalReplacementText: string | null;
  overridesFactChecker: boolean;
  agentReasoning: string;
  evidenceUrls: string[];
  humanComment: {
    reason: string;
    conflictingSources?: Array<{ url?: string; what_it_says?: string } | Record<string, string>>;
    suggestedOptions?: string[];
  } | null;
}

export interface ProposedNewFinding {
  causedBy: number;
  section_heading: string;
  original_text: string;
  replacement_text: string;
  reasoning: string;
  severity: "warning" | "suggestion" | string;
  action: "replace" | "remove" | string;
}

export interface AutoReview {
  status?: "queued" | "running" | "completed" | "failed" | string;
  completedAt?: string;
  decisions?: AutoReviewDecision[];
  proposedNewFindings?: ProposedNewFinding[];
  summary?: {
    accepted?: number;
    rejected?: number;
    escalated?: number;
    overrodeFactChecker?: number;
    newFindingsProposed?: number;
  };
}

type FilterType = "all" | "critical" | "warning" | "suggestion" | "escalated";

interface FactCheckSidebarContentProps {
  findings: FactCheckFinding[];
  onSelectFinding?: (finding: FactCheckFinding) => void;
  activeFindingId?: number | null;
  onApplyAction?: (finding: FactCheckFinding) => void;
  onDeclineAction?: (finding: FactCheckFinding) => void;
  autoReview?: AutoReview | null;
  // True while a backend /article/apply-finding call is in flight. Disables
  // every apply/decline button so a second action can't queue a stale
  // /article/update behind the apply and clobber its result.
  isApplyInProgress?: boolean;
}

const severityColor = (severity: string) => {
  if (severity === "critical") return { bg: "#FEE2E2", text: "#B91C1C" };
  if (severity === "warning") return { bg: "#FEF3C7", text: "#B45309" };
  return { bg: "#E0E7FF", text: "#3730A3" };
};

const categoryLabel = (category: string) =>
  category
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");

type AgentVerdict = "accept" | "override" | "reject" | "escalate" | "unknown";

const classifyDecision = (d?: AutoReviewDecision | null): AgentVerdict => {
  if (!d) return "unknown";
  if (d.decision === "accept") return d.overridesFactChecker ? "override" : "accept";
  if (d.decision === "reject") return "reject";
  if (d.decision === "escalate") return "escalate";
  return "unknown";
};

const verdictStyles: Record<AgentVerdict, { bg: string; border: string; text: string; chipBg: string; label: string; Icon: React.ElementType }> = {
  accept:   { bg: "#ECFDF5", border: "#10B981", text: "#065F46", chipBg: "#D1FAE5", label: "Agent: Accept",   Icon: CheckCircleOutlineIcon },
  override: { bg: "#F5F3FF", border: "#8B5CF6", text: "#5B21B6", chipBg: "#EDE9FE", label: "Agent: Override", Icon: EditOutlinedIcon },
  reject:   { bg: "#FEF2F2", border: "#EF4444", text: "#991B1B", chipBg: "#FEE2E2", label: "Agent: Reject",   Icon: HighlightOffIcon },
  escalate: { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E", chipBg: "#FEF3C7", label: "Agent: Escalate", Icon: HelpOutlineIcon },
  unknown:  { bg: "#F3F4F6", border: "#9CA3AF", text: "#374151", chipBg: "#E5E7EB", label: "Agent: —",        Icon: SmartToyOutlinedIcon },
};

const FactCheckSidebarContent: React.FC<FactCheckSidebarContentProps> = ({
  findings = [],
  onSelectFinding,
  activeFindingId,
  onApplyAction,
  onDeclineAction,
  autoReview,
  isApplyInProgress = false,
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedReasoning, setExpandedReasoning] = useState<Record<number, boolean>>({});
  const [dismissedProposed, setDismissedProposed] = useState<Record<number, boolean>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Scroll to the active card whenever the active finding changes (e.g. user clicked underlined text)
  useEffect(() => {
    if (activeFindingId == null) return;
    const el = cardRefs.current[String(activeFindingId)];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeFindingId]);

  const decisionsById = useMemo<Record<number, AutoReviewDecision>>(() => {
    const map: Record<number, AutoReviewDecision> = {};
    (autoReview?.decisions || []).forEach((d) => {
      const id = Number(d?.findingId);
      if (Number.isFinite(id)) map[id] = d;
    });
    return map;
  }, [autoReview]);

  const escalatedCount = useMemo(
    () => findings.filter((f) => decisionsById[f.id]?.decision === "escalate").length,
    [findings, decisionsById]
  );

  const proposedFindings = autoReview?.proposedNewFindings || [];

  const criticalCount = findings.filter((f) => f.severity === "critical").length;
  const warningCount = findings.filter((f) => f.severity === "warning").length;
  const suggestionCount = findings.filter((f) => f.severity === "suggestion").length;

  const filteredFindings = useMemo(() => {
    if (activeFilter === "all") return findings;
    if (activeFilter === "escalated") {
      return findings.filter((f) => decisionsById[f.id]?.decision === "escalate");
    }
    return findings.filter((f) => f.severity === activeFilter);
  }, [activeFilter, findings, decisionsById]);

  const handleCopyText = async (
    event: React.MouseEvent,
    text: string,
    key: string
  ) => {
    event.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? null : prev));
      }, 1500);
    } catch (error) {
      console.error("Failed to copy text", error);
    }
  };

  const renderReasoningWithLinks = (text: string) => {
    const parts = text.split(/(finding #\d+)/gi);
    return parts.map((part, idx) => {
      const match = part.match(/^finding #(\d+)$/i);
      if (match) {
        const refId = parseInt(match[1], 10);
        return (
          <Link
            key={idx}
            component="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const el = cardRefs.current[String(refId)];
              el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
              const target = findings.find((f) => f.id === refId);
              if (target) onSelectFinding?.(target);
            }}
            sx={{ fontSize: "inherit", color: "#6366F1", fontWeight: 600, cursor: "pointer", verticalAlign: "baseline" }}
          >
            {part}
          </Link>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "20px", fontFamily: "Inter", color: "#121828", mb: 0.5 }}>
          Fact Check Findings
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#6B7280", fontFamily: "Inter" }}>
          Review issues and apply corrections before publishing.
        </Typography>
      </Box>

      {autoReview && (
        <Card
          sx={{
            p: 1.5,
            mb: 2,
            border: "1px solid #E5E7EB",
            backgroundColor: "#F9FAFB",
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 16, color: "#6366F1" }} />
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#374151" }}>
              AGENT AUTO-REVIEW
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#065F46 !important" }} />} label={`${autoReview.summary?.accepted ?? 0} accept`} sx={{ backgroundColor: "#D1FAE5", color: "#065F46", fontWeight: 700 }} />
            <Chip size="small" icon={<HighlightOffIcon sx={{ fontSize: 14, color: "#991B1B !important" }} />} label={`${autoReview.summary?.rejected ?? 0} reject`} sx={{ backgroundColor: "#FEE2E2", color: "#991B1B", fontWeight: 700 }} />
            <Chip size="small" icon={<HelpOutlineIcon sx={{ fontSize: 14, color: "#92400E !important" }} />} label={`${autoReview.summary?.escalated ?? 0} escalate`} sx={{ backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />
            {(autoReview.summary?.overrodeFactChecker ?? 0) > 0 && (
              <Chip size="small" icon={<EditOutlinedIcon sx={{ fontSize: 14, color: "#5B21B6 !important" }} />} label={`${autoReview.summary?.overrodeFactChecker} override`} sx={{ backgroundColor: "#EDE9FE", color: "#5B21B6", fontWeight: 700 }} />
            )}
          </Box>
        </Card>
      )}

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Card
          onClick={() => setActiveFilter("critical")}
          sx={{ flex: "1 1 0", minWidth: 70, p: 1.5, cursor: "pointer", border: activeFilter === "critical" ? "2px solid #B91C1C" : "1px solid #E5E7EB", boxShadow: "none" }}
        >
          <Typography sx={{ fontSize: "12px", color: "#B91C1C", fontWeight: 600 }}>Critical</Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{criticalCount}</Typography>
        </Card>
        <Card
          onClick={() => setActiveFilter("warning")}
          sx={{ flex: "1 1 0", minWidth: 70, p: 1.5, cursor: "pointer", border: activeFilter === "warning" ? "2px solid #B45309" : "1px solid #E5E7EB", boxShadow: "none" }}
        >
          <Typography sx={{ fontSize: "12px", color: "#B45309", fontWeight: 600 }}>Warning</Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{warningCount}</Typography>
        </Card>
        <Card
          onClick={() => setActiveFilter("suggestion")}
          sx={{ flex: "1 1 0", minWidth: 70, p: 1.5, cursor: "pointer", border: activeFilter === "suggestion" ? "2px solid #3730A3" : "1px solid #E5E7EB", boxShadow: "none" }}
        >
          <Typography sx={{ fontSize: "12px", color: "#3730A3", fontWeight: 600 }}>Suggestion</Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{suggestionCount}</Typography>
        </Card>
        {autoReview && (
          <Card
            onClick={() => setActiveFilter("escalated")}
            sx={{ flex: "1 1 0", minWidth: 70, p: 1.5, cursor: "pointer", border: activeFilter === "escalated" ? "2px solid #92400E" : "1px solid #E5E7EB", boxShadow: "none" }}
          >
            <Typography sx={{ fontSize: "12px", color: "#92400E", fontWeight: 600 }}>Escalated</Typography>
            <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{escalatedCount}</Typography>
          </Card>
        )}
      </Box>

      {activeFilter !== "all" && (
        <Box sx={{ mb: 2, textAlign: "center" }}>
          <Button onClick={() => setActiveFilter("all")} size="small" sx={{ textTransform: "none" }}>
            Show all findings
          </Button>
        </Box>
      )}

      {filteredFindings.length === 0 ? (
        <Card sx={{ p: 2, border: "1px solid #E5E7EB", boxShadow: "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TaskAltIcon sx={{ color: "#16A34A" }} />
            <Typography sx={{ fontSize: "14px", color: "#111827" }}>No findings for this filter.</Typography>
          </Box>
        </Card>
      ) : (
        filteredFindings.map((finding) => {
          const color = severityColor(finding.severity);
          const normalizedAction = String(finding.action || "").trim().toLowerCase();
          const isReplaceAction = normalizedAction === "replace";
          const isRemoveAction = normalizedAction === "remove";
          const isFlagForReview = normalizedAction === "flag_for_review";
          const effectiveStatus = finding.status || (finding.applied ? "applied" : "pending");
          const isApplied = effectiveStatus === "applied";
          const isDeclined = effectiveStatus === "declined";
          const isResolved = isApplied || isDeclined;
          const decision = decisionsById[finding.id];
          const verdict = classifyDecision(decision);
          const vStyle = verdict !== "unknown" ? verdictStyles[verdict] : null;
          const VIcon = vStyle?.Icon;
          const isReasoningOpen = !!expandedReasoning[finding.id];
          const recommendApply = verdict === "accept" || verdict === "override";
          const recommendDecline = verdict === "reject";
          return (
            <Card
              key={`${finding.id}-${finding.original_text.slice(0, 24)}`}
              ref={(el) => { cardRefs.current[String(finding.id)] = el as HTMLDivElement | null; }}
              onClick={(event) => {
                const target = event.target as HTMLElement;
                if (target.closest('[data-disable-card-select="true"]')) return;
                onSelectFinding?.(finding);
              }}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #E5E7EB",
                boxShadow: "none",
                overflow: "hidden",
                cursor: onSelectFinding ? "pointer" : "default",
                ...(isDeclined && { opacity: 0.5 }),
                "&:hover": onSelectFinding
                  ? { borderColor: "#6366F1", boxShadow: "0 0 0 1px #6366F1 inset" }
                  : {},
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>
                    #{finding.id}
                  </Typography>
                  <Chip label={finding.severity.toUpperCase()} size="small" sx={{ backgroundColor: color.bg, color: color.text, fontWeight: 700 }} />
                  {vStyle && (
                    <Chip
                      icon={VIcon ? <VIcon sx={{ fontSize: 14, color: `${vStyle.text} !important` }} /> : undefined}
                      label={vStyle.label.replace("Agent: ", "")}
                      size="small"
                      sx={{ backgroundColor: vStyle.chipBg, color: vStyle.text, fontWeight: 700, borderLeft: `3px solid ${vStyle.border}` }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{categoryLabel(finding.category)}</Typography>
              </Box>

              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Section: {finding.section_heading || "Introduction"}</Typography>
              <Box
                data-disable-card-select="true"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>Original</Typography>
                <Tooltip title="Copy original text">
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      handleCopyText(event, finding.original_text, `original-${finding.id}`)
                    }
                    sx={{ p: "2px" }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {copiedKey === `original-${finding.id}` && (
                  <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600 }}>
                    Copied
                  </Typography>
                )}
              </Box>
              <Typography data-disable-card-select="true" sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text", wordBreak: "break-word" }}>
                {finding.original_text}
              </Typography>

              <Box
                data-disable-card-select="true"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                  {isRemoveAction
                    ? "Action: Remove"
                    : normalizedAction === "flag_for_review"
                    ? "Action: Review Needed"
                    : "Suggested Replacement"}
                </Typography>
                {finding.replacement_text && (
                  <Tooltip title="Copy replacement text">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(event) =>
                          handleCopyText(
                            event,
                            finding.replacement_text || "",
                            `replacement-${finding.id}`
                          )
                        }
                        sx={{ p: "2px" }}
                        disabled={!finding.replacement_text}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {copiedKey === `replacement-${finding.id}` && (
                  <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600 }}>
                    Copied
                  </Typography>
                )}
              </Box>
              <Typography data-disable-card-select="true" sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text", wordBreak: "break-word" }}>
                {finding.replacement_text || "No replacement text provided."}
              </Typography>

              {decision && vStyle && (
                <Box
                  data-disable-card-select="true"
                  sx={{
                    backgroundColor: vStyle.bg,
                    border: `1px solid ${vStyle.border}33`,
                    borderLeft: `3px solid ${vStyle.border}`,
                    borderRadius: 1,
                    p: 1.25,
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                    {VIcon && <VIcon sx={{ fontSize: 16, color: vStyle.text }} />}
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: vStyle.text }}>
                      {vStyle.label}
                      {verdict === "override" && " — agent suggests different wording"}
                      {verdict === "escalate" && " — needs your judgment"}
                      {verdict === "reject" && " — original text appears correct"}
                      {verdict === "accept" && " — fact-checker is right, apply as-is"}
                    </Typography>
                  </Box>

                  {verdict === "override" && decision.finalReplacementText && (
                    <Box sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: vStyle.text, mb: 0.25 }}>
                        Agent's preferred wording:
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: "#111827", backgroundColor: "#FFFFFF", border: "1px dashed #C4B5FD", borderRadius: 0.5, p: 0.75, userSelect: "text", wordBreak: "break-word" }}>
                        {decision.finalReplacementText}
                      </Typography>
                    </Box>
                  )}

                  {verdict === "escalate" && decision.humanComment && (
                    <Box sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontSize: "12px", color: "#111827", userSelect: "text", wordBreak: "break-word" }}>
                        {decision.humanComment.reason}
                      </Typography>
                      {Array.isArray(decision.humanComment.conflictingSources) && decision.humanComment.conflictingSources.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: vStyle.text, mb: 0.25 }}>
                            Conflicting sources:
                          </Typography>
                          {decision.humanComment.conflictingSources.map((src, idx) => {
                            const url = (src as any)?.url || "";
                            const says = (src as any)?.what_it_says || (src as any)?.whatItSays || "";
                            return (
                              <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 0.25 }}>
                                <LinkIcon sx={{ fontSize: 12, color: vStyle.border, mt: "3px", flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  {url && (
                                    <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "11px", wordBreak: "break-all" }}>
                                      {url}
                                    </Link>
                                  )}
                                  {says && (
                                    <Typography sx={{ fontSize: "11px", color: "#4B5563", wordBreak: "break-word" }}>
                                      {says}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      {Array.isArray(decision.humanComment.suggestedOptions) && decision.humanComment.suggestedOptions.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: vStyle.text, mb: 0.25 }}>
                            Suggested options:
                          </Typography>
                          {decision.humanComment.suggestedOptions.map((opt, idx) => (
                            <Typography key={idx} sx={{ fontSize: "11px", color: "#4B5563", wordBreak: "break-word", pl: 1 }}>
                              • {opt}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {decision.agentReasoning && (
                    <Box>
                      <Button
                        size="small"
                        onClick={() =>
                          setExpandedReasoning((prev) => ({ ...prev, [finding.id]: !prev[finding.id] }))
                        }
                        sx={{ textTransform: "none", fontSize: "11px", color: vStyle.text, p: 0, minWidth: "unset", "&:hover": { backgroundColor: "transparent" } }}
                      >
                        {isReasoningOpen ? "Hide agent reasoning ▴" : "Show agent reasoning ▾"}
                      </Button>
                      {isReasoningOpen && (
                        <Typography sx={{ fontSize: "12px", color: "#374151", mt: 0.5, userSelect: "text", wordBreak: "break-word" }}>
                          {decision.agentReasoning}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {Array.isArray(decision.evidenceUrls) && decision.evidenceUrls.length > 0 && (
                    <Box sx={{ mt: 0.75 }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: vStyle.text, mb: 0.25 }}>
                        Agent evidence:
                      </Typography>
                      {decision.evidenceUrls.map((url, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LinkIcon sx={{ fontSize: 12, color: vStyle.border, flexShrink: 0 }} />
                          <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "11px", wordBreak: "break-all" }}>
                            {url}
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                {/* Primary CTA: Apply/Review */}
                <Button
                  size="small"
                  variant={recommendApply && !isResolved ? "contained" : "outlined"}
                  data-disable-card-select="true"
                  onClick={(event) => {
                    event.stopPropagation();
                    onApplyAction?.(finding);
                  }}
                  disabled={
                    isResolved ||
                    isApplyInProgress ||
                    !onApplyAction ||
                    ((isReplaceAction || (isFlagForReview && finding.replacement_text?.trim())) && !finding.replacement_text?.trim())
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: "unset",
                    px: 1.5,
                    py: 0.5,
                    ...(isApplied && {
                      backgroundColor: "#22C55E",
                      "&.Mui-disabled": {
                        backgroundColor: "#22C55E",
                        color: "#FFFFFF",
                        opacity: 1,
                      },
                    }),
                  }}
                >
                  {isApplied
                    ? "Applied"
                    : (isReplaceAction || (isFlagForReview && finding.replacement_text?.trim()))
                    ? "Apply Replace"
                    : isRemoveAction
                    ? "Apply Remove"
                    : "Mark Reviewed"}
                </Button>

                {/* Override: apply agent's preferred wording */}
                {verdict === "override" && decision?.finalReplacementText && !isResolved && (
                  <Button
                    size="small"
                    variant="contained"
                    data-disable-card-select="true"
                    onClick={(event) => {
                      event.stopPropagation();
                      onApplyAction?.({ ...finding, replacement_text: decision.finalReplacementText! });
                    }}
                    disabled={!onApplyAction || isApplyInProgress}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: "unset",
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: "#8B5CF6",
                      "&:hover": { backgroundColor: "#7C3AED" },
                    }}
                  >
                    Apply Agent's
                  </Button>
                )}

                {/* Decline button */}
                <Button
                  size="small"
                  variant={recommendDecline && !isResolved ? "contained" : "outlined"}
                  color={recommendDecline && !isResolved ? "error" : "primary"}
                  data-disable-card-select="true"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeclineAction?.(finding);
                  }}
                  disabled={isResolved || isApplyInProgress || !onDeclineAction}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: "unset",
                    px: 1.5,
                    py: 0.5,
                    ...(isDeclined && {
                      borderColor: "#9CA3AF",
                      color: "#6B7280",
                      "&.Mui-disabled": {
                        borderColor: "#9CA3AF",
                        color: "#6B7280",
                        opacity: 1,
                      },
                    }),
                  }}
                >
                  {isDeclined ? "Declined" : "Decline"}
                </Button>
              </Box>

              <Box data-disable-card-select="true" sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: "#6B7280", mt: "2px", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "12px", color: "#4B5563", userSelect: "text", flex: 1, minWidth: 0, wordBreak: "break-word" }}>{renderReasoningWithLinks(finding.reasoning)}</Typography>
                <Tooltip title="Copy reasoning">
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      handleCopyText(event, finding.reasoning, `reasoning-${finding.id}`)
                    }
                    sx={{ p: "2px", flexShrink: 0 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {copiedKey === `reasoning-${finding.id}` && (
                  <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600, flexShrink: 0 }}>
                    Copied
                  </Typography>
                )}
              </Box>

              {finding.source && (() => {
                const parts = finding.source
                  .split(/\s*;\s*/)
                  .map((u) => u.trim())
                  .filter((u) => u.length > 0);
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {parts.map((part, idx) => {
                      const isUrl = /^https?:\/\//i.test(part);
                      return (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LinkIcon sx={{ fontSize: 14, color: "#6366F1", flexShrink: 0 }} />
                          {isUrl ? (
                            <Link href={part} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "12px", wordBreak: "break-all", minWidth: 0 }}>
                              {part}
                            </Link>
                          ) : (
                            <Typography sx={{ fontSize: "12px", color: "#4B5563", wordBreak: "break-word", minWidth: 0 }}>{part}</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Card>
          );
        })
      )}

      {proposedFindings.length > 0 && activeFilter === "all" && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 18, color: "#5B21B6" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#5B21B6" }}>
              Agent-Proposed Findings
            </Typography>
            <Chip
              label={proposedFindings.length}
              size="small"
              sx={{ backgroundColor: "#EDE9FE", color: "#5B21B6", fontWeight: 700, height: 20 }}
            />
          </Box>
          <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 1.5 }}>
            New issues the agent flagged while reviewing other findings.
          </Typography>

          {proposedFindings.map((p, idx) => {
            if (dismissedProposed[idx]) return null;
            const sev = severityColor(p.severity);
            const synthetic: FactCheckFinding = {
              id: 100000 + idx,
              section_heading: p.section_heading,
              category: "agent_proposed",
              severity: p.severity,
              original_text: p.original_text,
              replacement_text: p.replacement_text || null,
              reasoning: p.reasoning,
              source: "",
              action: p.action,
              status: "pending",
            };
            return (
              <Card
                key={`proposed-${idx}`}
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('[data-disable-card-select="true"]')) return;
                  onSelectFinding?.(synthetic);
                }}
                sx={{
                  p: 2,
                  mb: 2,
                  border: "1px dashed #8B5CF6",
                  backgroundColor: "#FBFAFF",
                  boxShadow: "none",
                  cursor: onSelectFinding ? "pointer" : "default",
                  "&:hover": onSelectFinding
                    ? { borderColor: "#7C3AED", boxShadow: "0 0 0 1px #7C3AED inset" }
                    : {},
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      icon={<SmartToyOutlinedIcon sx={{ fontSize: 14, color: "#5B21B6 !important" }} />}
                      label="AGENT-PROPOSED"
                      size="small"
                      sx={{ backgroundColor: "#EDE9FE", color: "#5B21B6", fontWeight: 700 }}
                    />
                    <Chip label={p.severity.toUpperCase()} size="small" sx={{ backgroundColor: sev.bg, color: sev.text, fontWeight: 700 }} />
                  </Box>
                  <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>
                    From #{p.causedBy}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>
                  Section: {p.section_heading || "Introduction"}
                </Typography>

                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", mb: 0.5 }}>
                  Original
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text", wordBreak: "break-word" }}>
                  {p.original_text}
                </Typography>

                {p.action !== "remove" && p.replacement_text && (
                  <>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", mb: 0.5 }}>
                      Suggested Replacement
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text", wordBreak: "break-word" }}>
                      {p.replacement_text}
                    </Typography>
                  </>
                )}

                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(event) => {
                      event.stopPropagation();
                      onApplyAction?.(synthetic);
                    }}
                    disabled={!onApplyAction || isApplyInProgress || (p.action === "replace" && !p.replacement_text?.trim())}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: "unset",
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: "#8B5CF6",
                      "&:hover": { backgroundColor: "#7C3AED" },
                    }}
                  >
                    {p.action === "remove" ? "Apply Remove" : "Apply Replace"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDismissedProposed((prev) => ({ ...prev, [idx]: true }));
                    }}
                    sx={{ textTransform: "none", fontWeight: 600, minWidth: "unset", px: 1.5, py: 0.5 }}
                  >
                    Decline
                  </Button>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: "#6B7280", mt: "2px", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "12px", color: "#4B5563", userSelect: "text", flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                    {p.reasoning}
                  </Typography>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FactCheckSidebarContent;
